import type { DiretoriaIndicator } from '@/data/diretoriaTypes';
import type {
  DiretoriaFaturamentoBrutoPayload,
  DiretoriaInadimplenciaPayload,
  DiretoriaInfracoesPayload,
} from '@/lib/diretoriaErpIndicatorBuilders';

const emptyEstado = { qtd: 0, valor: 0 };

const fmtPct = (value: number | null): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1).replace('.', ',')}%`;
};

const fmtMi = (value: number, formatCurrency: (n: number) => string): string => {
  if (Math.abs(value) >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(2).replace('.', ',')} mi`;
  }
  return formatCurrency(value);
};

const fmtQtdValor = (qtd: number, valor: number, formatCurrency: (n: number) => string): string =>
  `${qtd} · ${formatCurrency(valor)}`;

function hasChartOrTable(
  series: Array<{ name: string; valor: number; valor2?: number }>,
  tableRows: string[][],
  pieData?: Array<{ name: string; value: number }>
): boolean {
  return series.length > 0 || tableRows.length > 0 || (pieData?.length ?? 0) > 0;
}

export function buildFaturamentoBrutoIndicatorFromCobranca(
  base: DiretoriaIndicator,
  payload: DiretoriaFaturamentoBrutoPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const mensal = payload.mensal ?? [];
  const porEmpresa = payload.porEmpresa ?? [];
  const series = mensal.map((row) => ({ name: row.name, valor: Math.round(row.totalPago) }));
  const tableRows = porEmpresa
    .slice(0, 20)
    .map((row) => [row.nomeEmpresa, formatCurrency(row.totalPago)]);
  return {
    ...base,
    shortDescription: 'Faturamento bruto (pagamentos em títulos) — PortalFly.',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Anual (YTD)',
        value: fmtMi(payload.ytdTotal ?? 0, formatCurrency),
        hint: 'Ano de dataTo · PortalFly',
        tone: (payload.ytdTotal ?? 0) > 0 ? 'good' : 'neutral',
      },
      {
        label: 'Período / mês ref.',
        value: formatCurrency(payload.mesAtual ?? 0),
        hint: 'Pagamentos no intervalo (ou mês de dataTo)',
        tone: (payload.mesAtual ?? 0) > 0 ? 'good' : 'neutral',
      },
      {
        label: 'Var. vs período ant.',
        value: fmtPct(payload.variacaoPct ?? null),
        hint: 'PortalFly (títulos)',
        tone:
          payload.variacaoPct == null ? 'neutral' : payload.variacaoPct >= 0 ? 'good' : 'bad',
      },
    ],
    chartKind: 'line',
    chartTitle: 'Evolução mensal (ano de referência)',
    series,
    barSeriesKey: 'valor',
    seriesLabels: { valor: 'Faturamento' },
    tableHeaders: ['Unidade', 'Faturamento'],
    tableRows,
    footnote:
      'PortalFly: apenas statusLocacao=APROVADA; historico_pagamentos (CONFIRMADO/PARCIAL) ou valorPago+ultimoPagamento; exclui is_multa e “Recebimento Multa”. Série mensal = ano de dataTo; tabela = empresas no mês de dataTo.',
    querySql: payload.querySql,
    dataSource: 'cobranca',
    hasData:
      hasChartOrTable(series, tableRows) ||
      (payload.ytdTotal ?? 0) > 0 ||
      (payload.mesAtual ?? 0) > 0,
  };
}

export function buildInadimplenciaIndicatorFromCobrancaLocal(
  base: DiretoriaIndicator,
  payload: DiretoriaInadimplenciaPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const faixas = payload.faixas ?? [];
  const series = faixas.map((row) => ({
    name: row.faixa,
    valor: row.qtd,
    valor2: Math.round(row.saldo),
  }));
  const tableRows = faixas.map((row) => [row.faixa, String(row.qtd), formatCurrency(row.saldo)]);
  return {
    ...base,
    shortDescription: 'Títulos vencidos/parciais com locação APROVADA — PortalFly.',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Saldo vencido',
        value: formatCurrency(payload.saldoVencido ?? 0),
        hint: 'As-of dataTo',
        tone: (payload.saldoVencido ?? 0) > 0 ? 'bad' : 'good',
      },
      {
        label: 'Títulos vencidos',
        value: String(payload.titulosVencidos ?? 0),
        hint: 'As-of dataTo',
        tone: (payload.titulosVencidos ?? 0) > 0 ? 'bad' : 'good',
      },
      {
        label: 'Novos no mês',
        value: String(payload.novosMes ?? 0),
        hint: 'Vencimento no mês de dataTo',
        tone: (payload.novosMes ?? 0) > 0 ? 'bad' : 'good',
      },
    ],
    chartKind: 'barGrouped',
    chartTitle: 'Aging por faixa de atraso',
    series,
    seriesLabels: { valor: 'Qtd.', valor2: 'Saldo' },
    tableHeaders: ['Faixa de atraso', 'Qtd.', 'Saldo'],
    tableRows,
    footnote:
      'PortalFly / títulos: apenas locações APROVADA; VENCIDO/PARCIAL; as-of dataTo. Aging civil 1–2 / 3–6 / 7+ dias. Exclui multas (is_multa e texto). Tabela por empresa usa o mesmo filtro.',
    querySql: payload.querySql,
    dataSource: 'cobranca',
    hasData: true,
  };
}

export function buildInfracoesIndicatorFromCobranca(
  base: DiretoriaIndicator,
  payload: DiretoriaInfracoesPayload,
  formatCurrency: (value: number) => string
): DiretoriaIndicator {
  const pagasMes = payload.pagasMes ?? emptyEstado;
  const vencidas = payload.vencidas ?? emptyEstado;
  const aVencer = payload.aVencer ?? emptyEstado;
  const mensal = payload.mensal ?? [];
  const porEmpresa = payload.porEmpresa ?? [];
  const media = (qtd: number, valor: number) => (qtd > 0 ? valor / qtd : 0);
  const row = (estado: string, qtd: number, valor: number) => [
    estado,
    String(qtd),
    formatCurrency(valor),
    formatCurrency(media(qtd, valor)),
  ];
  const series =
    mensal.length > 0
      ? mensal.map((m) => ({
          name: m.name,
          valor: m.qtdPagas,
          valor2: Math.round(m.valorPagas),
        }))
      : [
          { name: 'Pagas (mês ref.)', valor: pagasMes.qtd, valor2: Math.round(pagasMes.valor) },
          { name: 'Vencidas', valor: vencidas.qtd, valor2: Math.round(vencidas.valor) },
          { name: 'A vencer', valor: aVencer.qtd, valor2: Math.round(aVencer.valor) },
        ];
  const tableRows =
    porEmpresa.length > 0
      ? porEmpresa.slice(0, 20).map((e) => [
          e.nomeEmpresa,
          String(e.qtdPagas),
          String(e.qtdVencidas),
          String(e.qtdAVencer),
          formatCurrency(e.valorAberto),
        ])
      : [
          row('Pagas (mês ref.)', pagasMes.qtd, pagasMes.valor),
          row('Vencidas', vencidas.qtd, vencidas.valor),
          row('A vencer', aVencer.qtd, aVencer.valor),
        ];
  const totalQtd = pagasMes.qtd + vencidas.qtd + aVencer.qtd;
  const totalValor = pagasMes.valor + vencidas.valor + aVencer.valor;
  return {
    ...base,
    shortDescription: 'Títulos de multa no PortalFly (flag ou texto), por estado.',
    primaryKpiIndex: 0,
    kpis: [
      {
        label: 'Pagas (mês ref.)',
        value: fmtQtdValor(pagasMes.qtd, pagasMes.valor, formatCurrency),
        hint: 'FECHADO + valorPago; data = pagamento ou vencimento',
        tone: 'good',
      },
      {
        label: 'Vencidas',
        value: fmtQtdValor(vencidas.qtd, vencidas.valor, formatCurrency),
        hint: 'Vencimento < dataTo',
        tone: vencidas.qtd > 0 ? 'bad' : 'good',
      },
      {
        label: 'Média / multa',
        value: formatCurrency(media(totalQtd, totalValor)),
        hint: 'Valor total ÷ qtd. (todos os estados)',
        tone: 'neutral',
      },
    ],
    chartKind: mensal.length > 0 ? 'line' : 'barGrouped',
    chartTitle: mensal.length > 0 ? 'Multas pagas mês a mês' : 'Multas por estado',
    series,
    seriesLabels: { valor: 'Qtd.', valor2: 'Valor' },
    tableHeaders:
      porEmpresa.length > 0
        ? ['Empresa', 'Pagas', 'Vencidas', 'A vencer', 'Aberto']
        : ['Estado', 'Qtd.', 'Valor', 'Média'],
    tableRows,
    footnote:
      'PortalFly: is_multa=1 ou texto Multa/Recebimento Multa/tipo. Pagas = FECHADO com valorPago; data = COALESCE(ultimoPagamento, vencimento). Vencidas / a vencer as-of dataTo.',
    querySql: payload.querySql,
    dataSource: 'cobranca',
    hasData: totalQtd > 0,
  };
}
